// The ONE module in the website that imports @bookingdei/api-client (CB-2,
// rule 7.1-3). Every screen calls `api` from here — never a raw request, never a
// second client. Swapping the dev mock for the live api Worker is a config change
// (VITE_API_BASE_URL / VITE_USE_MOCK), not a code change.
import { createClient, ApiError } from '@bookingdei/api-client/client';
import type { Client } from '@bookingdei/api-client/client';
import type { components } from '@bookingdei/api-client/types';

// The switchable-role enum from the contract (auth.yaml#/Role) — same source the
// api Worker validates against, so a typo can't reach the wire.
type Role = components['schemas']['Role'];

// Base URL includes the `/v1` prefix (AP-D3). In production it is injected at build
// time; the mock ignores it.
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8787/v1';

// The website runs against a local mock that speaks the same wire contract (same
// envelope, same schemas) until pointed at the live api Worker. Defaults on in dev.
const useMock =
  (import.meta.env.VITE_USE_MOCK ?? (import.meta.env.DEV ? 'true' : 'false')) === 'true';

// The mock transport and its seed data are ~77 KB of source that production never
// runs. A static import shipped all of it in the entry bundle regardless — verified
// by finding seed strings in the built output. Loading it through a dynamic import
// puts it in its own chunk that is fetched only when a mock request is actually made,
// so a production visitor never downloads it.
const mockFetch: typeof fetch = async (input, init) => {
  const { mockTransport } = await import('./mock/mock-transport');
  return mockTransport(input as Request, init);
};

// Access token in memory (AP-D4) — never localStorage. The website is the single
// login entry: on OTP verify the api Worker sets the HttpOnly refresh cookie, which
// `credentials: 'include'` lets the browser store and then share (same-site) with the
// app / merchant / admin surfaces — that is what makes one sign-in serve every surface.
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
  writeSessionHint(token !== null);
}

// A session HINT, not a credential: '1' while this browser has signed in on the website
// and not signed out (or failed a refresh) since. The refresh cookie is HttpOnly, so the
// page cannot see it; without the hint every signed-out visit asked a session-only
// endpoint, got a 401, tried a refresh and got a second 401. With it, a visitor who never
// signed in here makes no session request at all. The dev mock answers without a session.
const SESSION_HINT = 'bd.session';
function writeSessionHint(on: boolean): void {
  try {
    if (on) localStorage.setItem(SESSION_HINT, '1');
    else localStorage.removeItem(SESSION_HINT);
  } catch {
    /* storage blocked: the hint is only an optimisation */
  }
}
export function mayHaveSession(): boolean {
  if (accessToken !== null || useMock) return true;
  try {
    return localStorage.getItem(SESSION_HINT) === '1';
  } catch {
    return false;
  }
}
export function hasAccessToken(): boolean {
  return accessToken !== null;
}

// A bare client used ONLY for POST /auth/refresh and /auth/logout — no getAccessToken
// and no refresher of its own, so a failed refresh can never recurse into another
// refresh. In dev it speaks to the same mock transport as `api`.
const refreshClient: Client = createClient({
  baseUrl,
  defaultLang: 'en',
  ...(useMock ? { fetch: mockFetch } : { credentials: 'include' as const }),
});

// Restore the shared session on this surface: if the visitor still holds the
// same-site HttpOnly refresh cookie (from a prior sign-in on any surface), the
// api-client calls this on a 401, mints a fresh access token, and retries — so a
// returning visitor mid-booking is signed in without re-entering an OTP.
async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = (await refreshClient.POST('/auth/refresh', { body: { client: 'web' } })) as {
      access_token?: string;
    };
    setAccessToken(res.access_token ?? null);
    return res.access_token ?? null;
  } catch {
    setAccessToken(null);
    return null;
  }
}

export const api: Client = createClient({
  baseUrl,
  defaultLang: 'en',
  getAccessToken: () => accessToken,
  ...(useMock ? { fetch: mockFetch } : { credentials: 'include' as const, refreshAccessToken }),
});

// Re-scope the session to another role the account holds (the "Continue as…" picker,
// M32). The api Worker validates the role against the live tables, mints a fresh
// access token, and rotates the shared bd_refresh cookie so the target surface boots
// already in that role. The website is where the choice is first made — right after
// login — mirroring dash-user's in-dashboard switcher.
export async function switchRole(role: Role): Promise<void> {
  const res = (await api.POST('/auth/switch-role', { body: { role, client: 'web' } })) as {
    access_token?: string;
  };
  setAccessToken(res.access_token ?? null);
}

// End the shared session: clear the refresh cookie server-side, then drop the
// in-memory token. Best-effort — the token is dropped either way.
export async function signOut(): Promise<void> {
  try {
    await refreshClient.POST('/auth/logout');
  } catch {
    /* best-effort — the cookie is cleared server-side; the token is dropped either way */
  }
  setAccessToken(null);
}

export { ApiError };
